// src/pages/Moderation/ChannelsViolationsPage/ui/ChannelsFilters/ChannelsFilters.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { DebounceInput } from 'react-debounce-input';
import { GetFlagsResponse } from 'redux/reducers/moderation/types';
import { Select } from 'ui/NewSelect/Select';
import { ChannelsViolationsFilter } from '../model/types';
import { SortState } from '../model/useChannelsViolations';
import './ChannelsFilters.scss';

type SelectOption<T extends string = string> = { id: T; label: string };

type Props = {
  value: ChannelsViolationsFilter;
  sort: SortState;
  restrictionOptions: SelectOption<string>[];
  onChange: (v: ChannelsViolationsFilter) => void;
  onSortChange: (s: SortState) => void;
  onReset: () => void;
  flags: GetFlagsResponse | null;
};

const ORDER_OPTIONS: SelectOption<'created_at' | 'title'>[] = [
  { id: 'created_at', label: 'Created at' },
  { id: 'title', label: 'Title' },
];

export const ChannelsFilters: React.FC<Props> = ({
  value,
  sort,
  restrictionOptions,
  onChange,
  onSortChange,
  onReset,
  flags,
}) => {
  const [local, setLocal] = useState<ChannelsViolationsFilter>(value);

  // ✅ якщо value прийшов новий (наприклад reset або з URL) — підхоплюємо
  useEffect(() => {
    setLocal(value);
  }, [value]);

  const update = (patch: Partial<ChannelsViolationsFilter>) => {
    const next = { ...local, ...patch };
    setLocal(next);
    onChange(next);
  };

  // --- Search (debounce) ---
  const [search, setSearch] = useState(value.search ?? '');
  useEffect(() => setSearch(value.search ?? ''), [value.search]);

  const debounced = useDebounce(search, 300);

  useEffect(() => {
    update({ search: debounced });
  }, [debounced]);

  // --- Flags options ---
  const flagOptions: SelectOption<string>[] = useMemo(
    () =>
      (flags?.data ?? [])
        .filter((f) => f?.value != null)
        .map((f) => ({
          id: String(f.value),
          label: f.description || f.name || `Flag ${f.value}`,
        })),
    [flags],
  );

  return (
    <div className="cv-filters">
      {/* Complaint type / flag */}
      <Select
        options={flagOptions}
        value={typeof local.flag === 'number' ? String(local.flag) : undefined}
        placeholder="Complaint Type"
        onChange={(v: string) => update({ flag: v ? Number(v) : undefined })}
        className="cv-filters__field"
      />

      {/* Restriction */}
      {/* <Select
        options={restrictionOptions}
        value={local.restriction != null ? String(local.restriction) : undefined}
        placeholder="Restriction"
        onChange={(v: string) => update({ restriction: v ? Number(v) : undefined })}
        className="cv-filters__field"
      /> */}

      {/* Order by */}
      {/* <Select
        options={ORDER_OPTIONS}
        value={sort.orderBy}
        placeholder="Order by"
        onChange={(v: 'created_at' | 'title') => onSortChange({ ...sort, orderBy: v })}
        className="cv-filters__field"
      /> */}

      <label className="cv-filters__toggle">
        <input
          type="checkbox"
          checked={!!sort.asc}
          onChange={(e) => onSortChange({ ...sort, asc: e.target.checked })}
        />
        ASC
      </label>

      <div className="cv-filters__search">
        <DebounceInput
          className="cv-filters__search-input"
          placeholder="Search by id / title / link"
          debounceTimeout={1000}
          value={search || ''}
          onChange={(e) => setSearch(e.target.value)}
        />
        {!!search && (
          <button type="button" className="cv-filters__clear" onClick={() => setSearch('')}>
            ×
          </button>
        )}
      </div>

      <button type="button" className="cv-filters__btn" onClick={onReset}>
        Reset
      </button>
    </div>
  );
};

function useDebounce<T>(value: T, delay = 300): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}
